import type {
  Coding,
  Observation,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { clinicDay } from '@seen/core/dates'
import { encounterStartDay } from '@seen/core/derivedBindings'
import { ExtendedObservation } from '@seen/core/models/observation'
import { componentValue } from '@seen/core/models/observationComponents'
import { Participant } from '@seen/core/models/patients/participant'
import {
  contextVariables,
  type DerivationHistoryInput,
  type DerivationInputs,
  type DerivationRule,
  derivationInputs,
  historyInput,
  inputDefault,
  isOptionalInput,
  waitingInputs,
} from '@seen/core/observation-logic/derivations/rules'
import { OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM } from '@seen/core/observation-logic/instruments/records'
import {
  NOTE_NODE_REFERENCE_PREFIX,
  panelItemSourceId,
} from '@seen/core/observation-logic/noteCapture'
import {
  type ObservationValue,
  observationValueOf,
} from '@seen/core/observation-logic/value'
import { referenceStringToId } from '@seen/core/references'
import { observationTemplate } from '@seen/core/terminology/observationTemplates'
import type { Editor } from '@tiptap/core'
import { useEditorState } from '@tiptap/react'
import { useMemo, useSyncExternalStore } from 'react'
import { CONDITIONAL_DISPLAY_KEY } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import { hiddenNodeIds } from '#src/components/rich-text-editor/conditional-display/conditionalDisplayState.ts'
import {
  type NoteInputSource,
  noteInputSources,
} from '#src/components/rich-text-editor/ObservationNode/derived/derivedNode.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

// Where one input's value came from: a node in this note, the participant's
// latest record when the note has no node for it, an earlier record from
// chart history, the rule's customary default, or nowhere yet.
export interface DerivationSource {
  coding: Coding
  origin: 'note' | 'record' | 'history' | 'default' | 'none'
  // The record read, when there is one; a source held in the note is named
  // by its node (`note-node:<id>`).
  reference?: string
  value: ObservationValue | null
  // The day of a record read from the chart (YYYY-MM-DD).
  date?: string
  // Where a default comes from.
  note?: string
  // An input the rule computes without (see `DerivationRule.optional`).
  optional?: true
}

export interface DerivationInputState {
  inputs: DerivationInputs
  sources: DerivationSource[]
  // Inputs with no value, in rule order.
  missing: Coding[]
  // Inputs with no node in the note, which Add can insert.
  absentFromNote: Coding[]
  // The records the value is computed from.
  derivedFrom: Reference<Observation>[]
  // A read the inputs come from is still in flight, so a missing input may
  // only be unread yet.
  loading: boolean
  // A read the inputs come from failed, so a missing input is unknown
  // rather than absent.
  failed: boolean
}

const EMPTY_INPUTS: readonly Coding[] = []
const EMPTY_HISTORY: readonly DerivationHistoryInput[] = []

function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function useEditorDoc(editor: Editor): Editor['state']['doc'] {
  return useSyncExternalStore(
    (onChange) => {
      editor.on('update', onChange)
      return () => {
        editor.off('update', onChange)
      }
    },
    () => editor.state.doc
  )
}

function noteValue(
  source: NoteInputSource,
  records: ReadonlyMap<string, ExtendedObservation>,
  panelRecords: ReadonlyMap<string, ExtendedObservation>
): ObservationValue | null {
  if (source.panel) {
    if (source.panel.value) {
      return source.panel.value
    }
    const record = panelRecords.get(source.panel.member)
    return record ? observationValueOf(record) : null
  }
  const record = source.draft
    ? ExtendedObservation.fromFhirResource(source.draft)
    : source.reference
      ? records.get(source.reference)
      : undefined
  if (!record) {
    return null
  }
  if (!source.component) {
    return observationValueOf(record)
  }
  const value = componentValue(record.toFhirResource(), source.component)
  const unit = observationTemplate(
    record.codeableConcept.coding?.[0] ?? {}
  )?.components.find(
    (spec) => spec.coding.code === source.component?.code
  )?.unit
  const number = typeof value === 'number' ? value : Number(value)
  if (
    value === undefined ||
    typeof value === 'object' ||
    Number.isNaN(number)
  ) {
    return null
  }
  const numeric: ObservationValue = { kind: 'number', value: number }
  if (unit) {
    numeric.unit = unit
  }
  return numeric
}

// What a source node's value is derived from: a held answer is named by its
// node, which the server rewrites to the record it writes at sign.
function noteReference(
  source: NoteInputSource,
  panelRecords: ReadonlyMap<string, ExtendedObservation>
): string | undefined {
  if (source.panel) {
    return source.panel.value
      ? `${NOTE_NODE_REFERENCE_PREFIX}${panelItemSourceId(source.panel.nodeId, source.panel.member)}`
      : panelRecords.get(source.panel.member)?.reference.reference
  }
  if (source.draft) {
    return source.nodeId
      ? `${NOTE_NODE_REFERENCE_PREFIX}${source.nodeId}`
      : undefined
  }
  return source.reference ?? undefined
}

// The inputs a computed node's rule reads: the nearest node before it in
// the note for each, else the participant's latest record of that code,
// plus the registry variables (sex, age on the encounter day) for the
// participant and encounter.
export function useDerivationInputs({
  editor,
  getPos,
  rule,
  participant,
}: {
  editor: Editor
  getPos: () => number | undefined
  rule: DerivationRule | undefined
  participant: Reference<Patient> | undefined
}): DerivationInputState {
  const doc = useEditorDoc(editor)
  // Followed too, since a condition can hide a source without the document
  // changing (an answer elsewhere, a context arriving).
  const hidden = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      hiddenNodeIds(
        CONDITIONAL_DISPLAY_KEY.getState(current.state)?.result
      ).join(' '),
  })
  const ruleInputs = rule?.inputs ?? EMPTY_INPUTS
  // Inputs a node in the note can hold: not those read from history.
  const nodeInputs = useMemo(
    () =>
      rule ? ruleInputs.filter((coding) => !historyInput(rule, coding)) : [],
    [rule, ruleInputs]
  )
  const position = getPos()
  const fromNote = useMemo(() => {
    if (position === undefined) {
      return []
    }
    const hiddenIds = new Set(hidden ? hidden.split(' ') : [])
    const found = noteInputSources(doc, position, nodeInputs, (pos) =>
      hiddenIds.has(doc.nodeAt(pos)?.attrs.id)
    )
    // Indexed like the rule's inputs.
    return ruleInputs.map((coding) => found[nodeInputs.indexOf(coding)])
  }, [doc, position, ruleInputs, nodeInputs, hidden])

  const noteReferences = useMemo(
    () =>
      [
        ...new Set(
          fromNote.flatMap((source) =>
            source?.reference && !source.draft ? [source.reference] : []
          )
        ),
      ].sort(),
    [fromNote]
  )
  const noteSearch = useSearch(
    ExtendedObservation,
    noteReferences.length
      ? ExtendedObservation.queryByIds(noteReferences.map(referenceStringToId))
      : undefined,
    { silentErrors: true }
  )
  const noteRecords = noteSearch.data

  // Items of panels in the note whose answers are written as given.
  const panelMembers = useMemo(
    () =>
      [
        ...new Set(
          fromNote.flatMap((source) =>
            source?.panel && !source.panel.value ? [source.panel.member] : []
          )
        ),
      ].sort(),
    [fromNote]
  )
  const panelSearch = useSearch(
    ExtendedObservation,
    participant?.reference && panelMembers.length
      ? {
          identifier: panelMembers
            .map(
              (member) =>
                `${OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM}|${member}`
            )
            .join(','),
          patient: participant.reference,
          'status:not': 'entered-in-error',
        }
      : undefined,
    { silentErrors: true }
  )
  const panelRecordList = panelSearch.data

  const absentFromNote = useMemo(
    () => nodeInputs.filter((coding) => !fromNote[ruleInputs.indexOf(coding)]),
    [nodeInputs, ruleInputs, fromNote]
  )
  const latestSearch = useSearch(
    ExtendedObservation,
    participant?.reference && absentFromNote.length
      ? {
          patient: participant.reference,
          code: absentFromNote
            .map((coding) => `${coding.system ?? ''}|${coding.code ?? ''}`)
            .join(','),
          'status:not': 'entered-in-error',
          _sort: '-date',
          _count: 20,
        }
      : undefined,
    { silentErrors: true }
  )
  const latestRecords = latestSearch.data

  // Chart history inputs: records of their codes in the window before the
  // visit, oldest first.
  const encounter = editor.storage.registryVariable?.contexts.encounter
  const visitDay = encounterStartDay(encounter) ?? clinicDay()
  const histories = rule?.history ?? EMPTY_HISTORY
  const historyDays = Math.max(0, ...histories.map((entry) => entry.withinDays))
  const historySearch = useSearch(
    ExtendedObservation,
    participant?.reference && histories.length
      ? {
          patient: participant.reference,
          code: histories
            .map(({ of }) => `${of.system ?? ''}|${of.code ?? ''}`)
            .join(','),
          date: `ge${addDays(visitDay, -historyDays)}`,
          'status:not': 'entered-in-error',
          _sort: 'date',
          _count: 50,
        }
      : undefined,
    { silentErrors: true }
  )
  const historyRecords = historySearch.data

  const patientRead = useRead(Participant, participant?.reference)
  const patient = patientRead.data
  const fhirPatient = useMemo(() => patient?.toFhirResource(), [patient])
  const reads = [
    noteSearch,
    panelSearch,
    latestSearch,
    historySearch,
    patientRead,
  ]
  const loading = reads.some((read) => read.isLoading)
  const failed = reads.some((read) => read.isError)

  return useMemo(() => {
    const records = new Map(
      (noteRecords ?? []).map((record) => [record.reference.reference, record])
    )
    const panelRecords = new Map(
      (panelRecordList ?? []).flatMap((record) => {
        const member = record
          .toFhirResource()
          .identifier?.find(
            (identifier) =>
              identifier.system === OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM
          )?.value
        return member ? [[member, record] as const] : []
      })
    )
    const found: DerivationSource[] = ruleInputs.map((coding, index) => {
      const optional =
        rule && isOptionalInput(rule, coding) ? { optional: true as const } : {}
      const history = rule ? historyInput(rule, coding) : undefined
      if (history) {
        const earliest = (historyRecords ?? []).find((record) => {
          const day = record.toFhirResource().effectiveDateTime?.slice(0, 10)
          return (
            !!day &&
            day < visitDay &&
            day >= addDays(visitDay, -history.withinDays) &&
            record.codeableConcept.coding?.some(
              (candidate) => candidate.code === history.of.code
            )
          )
        })
        const value = earliest ? observationValueOf(earliest) : null
        return earliest && value
          ? {
              coding,
              origin: 'history',
              reference: earliest.reference.reference,
              value,
              date: earliest.toFhirResource().effectiveDateTime?.slice(0, 10),
              ...optional,
            }
          : { coding, origin: 'none', value: null, ...optional }
      }
      const source = fromNote[index]
      if (source) {
        const reference = noteReference(source, panelRecords)
        const fromNoteSource: DerivationSource = {
          ...optional,
          coding,
          origin: 'note',
          value: noteValue(source, records, panelRecords),
        }
        if (reference) {
          fromNoteSource.reference = reference
        }
        return fromNoteSource
      }
      const latest = (latestRecords ?? []).find((record) =>
        record.codeableConcept.coding?.some(
          (candidate) =>
            candidate.code === coding.code &&
            (!candidate.system ||
              !coding.system ||
              candidate.system === coding.system)
        )
      )
      const effective = latest?.toFhirResource().effectiveDateTime
      const day =
        effective && effective.length > 10 ? clinicDay(effective) : effective
      const value =
        latest && (!rule?.sameVisit || day === visitDay)
          ? observationValueOf(latest)
          : null
      return latest && value
        ? {
            ...optional,
            coding,
            origin: 'record',
            reference: latest.reference.reference,
            date: day,
            value,
          }
        : { ...optional, coding, origin: 'none', value: null }
    })
    // An input with no value, even a blank node for it, takes the rule's
    // default until one is recorded.
    const sources = found.map((source): DerivationSource => {
      const fallback =
        source.value === null && rule
          ? inputDefault(rule, source.coding)
          : undefined
      return fallback
        ? {
            coding: source.coding,
            origin: 'default',
            value: fallback.value,
            note: fallback.note,
          }
        : source
    })
    return {
      inputs: derivationInputs(
        sources.map(({ coding, value }) => ({ coding, value })),
        { variable: contextVariables({ patient: fhirPatient, encounter }) }
      ),
      sources,
      missing: rule ? waitingInputs(rule, sources) : [],
      absentFromNote,
      derivedFrom: sources.flatMap((source): Reference<Observation>[] =>
        source.reference && source.value !== null
          ? [{ reference: source.reference }]
          : []
      ),
      loading,
      failed,
    }
  }, [
    loading,
    failed,
    ruleInputs,
    fromNote,
    noteRecords,
    panelRecordList,
    latestRecords,
    historyRecords,
    visitDay,
    rule,
    fhirPatient,
    encounter,
    absentFromNote,
  ])
}
